import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, Clock, Award, ChevronDown, Check, Star, ArrowRight, X, Zap, Target, Compass, Users, Info } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';
import CurrencySelector from '../components/CurrencySelector';
import FreePreviewCTA from '../components/FreePreviewCTA';
import useLiveCounter from '../hooks/useLiveCounter';
import useTranslation from '../data/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';

const testIcons = {
  'disc': '/picto-disc.png',
  'bigfive': '/picto-bigfive.png',
  'via': '/picto-via.png',
  'schwartz': null,
  'enneagram': '/picto-enneagram.png',
  'mbti': '/picto-mbti.png',
};

const SchwartzIcon = () => (
  <svg viewBox="0 0 64 64" className="w-12 h-12 text-orange-500">
    <circle cx="32" cy="32" r="26" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.3"/>
    <path d="M32 8 A24 24 0 0 1 56 32" fill="none" stroke="#EF4444" strokeWidth="4" strokeLinecap="round"/>
    <path d="M56 32 A24 24 0 0 1 32 56" fill="none" stroke="#F59E0B" strokeWidth="4" strokeLinecap="round"/>
    <path d="M32 56 A24 24 0 0 1 8 32" fill="none" stroke="#22C55E" strokeWidth="4" strokeLinecap="round"/>
    <path d="M8 32 A24 24 0 0 1 32 8" fill="none" stroke="#3B82F6" strokeWidth="4" strokeLinecap="round"/>
    <circle cx="32" cy="32" r="10" fill="currentColor" opacity="0.2"/>
  </svg>
);

const AVATAR_COLORS = ['bg-orange-500', 'bg-purple-500', 'bg-blue-500'];

const InitialsAvatar = ({ name, color }) => {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white border-2 border-orange-500/30 ${color}`}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
};

function SalesLanding() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { format } = useCurrency();
  const liveCount = useLiveCounter();
  const [openFaq, setOpenFaq] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const l = t.landing;

  const handleGetStarted = (pack = 'complete') => {
    navigate(`/checkout?pack=${pack}`);
  };

  const trackCta = (variant) => {
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture('hero_cta_click', { variant });
      }
    } catch (e) { /* ignore */ }
  };

  const handleCheckout = async (packId) => {
    setCheckoutLoading(packId);
    navigate(`/checkout?pack=${packId}`);
    setCheckoutLoading(null);
  };

  const HOW_STEPS = [
    { emoji: '\uD83E\uDDE0', step: '01', title: l.step1Title, sub: l.step1Sub },
    { emoji: '\u26A1', step: '02', title: l.step2Title, sub: l.step2Sub },
    { emoji: '\uD83D\uDCCA', step: '03', title: l.step3Title, sub: l.step3Sub },
    { emoji: '\uD83C\uDFAF', step: '04', title: l.step4Title, sub: l.step4Sub },
  ];

  const PILLARS = [
    { title: l.pillar1Title, description: l.pillar1Desc },
    { title: l.pillar2Title, description: l.pillar2Desc },
    { title: l.pillar3Title, description: l.pillar3Desc },
  ];

  const PAIN_POINTS = [l.pain1, l.pain2, l.pain3, l.pain4, l.pain5];
  const SOLUTIONS = [l.solution1, l.solution2, l.solution3, l.solution4, l.solution5];

  const TESTS = [
    { id: 'disc', name: l.testsDiscName, description: l.testsDiscDesc },
    { id: 'bigfive', name: l.testsBigfiveName, description: l.testsBigfiveDesc },
    { id: 'enneagram', name: l.testsEnneagramName, description: l.testsEnneagramDesc },
    { id: 'via', name: l.testsViaName, description: l.testsViaDesc },
    { id: 'schwartz', name: l.testsSchwartzName, description: l.testsSchwartzDesc },
    { id: 'mbti', name: l.testsMbtiName, description: l.testsMbtiDesc },
  ];

  const AI_FEATURES = [l.aiFeat1, l.aiFeat2, l.aiFeat3, l.aiFeat4, l.aiFeat5];
  const DUO_FEATURES = [l.aiDuoFeat1, l.aiDuoFeat2, l.aiDuoFeat3, l.aiDuoFeat4];

  const PRODUCTS = [
    {
      id: 'decouverte',
      name: l.discoveryName,
      subtitle: l.discoverySub,
      price: 9.99,
      features: [l.discoveryFeat1, l.discoveryFeat2, l.discoveryFeat3, l.discoveryFeat4],
      buttonText: l.discoveryBtn,
      buttonStyle: 'secondary',
    },
    {
      id: 'complete',
      name: l.completeName,
      subtitle: l.completeSub,
      price: 29.99,
      features: [l.completeFeat1, l.completeFeat2, l.completeFeat3, l.completeFeat4, l.completeFeat5, l.completeFeat6],
      buttonText: l.completeBtn,
      buttonStyle: 'primary',
      badge: l.completeBadge,
      badgeColor: 'orange',
      highlighted: true,
    },
    {
      id: 'duo',
      name: l.duoName,
      subtitle: l.duoSub,
      price: 39.99,
      originalPrice: 59.98,
      pricePerPersonAmount: 19.99,
      pricePerPersonSuffix: l.duoPerPersonSuffix,
      features: [l.duoFeat1, l.duoFeat2, l.duoFeat3, l.duoFeat4, l.duoFeat5, l.duoFeat6],
      buttonText: l.duoBtn,
      buttonStyle: 'primary-orange',
      badge: l.duoBadge,
      badgeColor: 'green',
    }
  ];

  const TESTIMONIALS = [
    { name: l.testimonial1Name, age: l.testimonial1Age, role: l.testimonial1Role, before: l.testimonial1Before, after: l.testimonial1After, quote: l.testimonial1Quote, rating: 5, color: AVATAR_COLORS[0] },
    { name: l.testimonial2Name, age: l.testimonial2Age, role: l.testimonial2Role, before: l.testimonial2Before, after: l.testimonial2After, quote: l.testimonial2Quote, rating: 5, color: AVATAR_COLORS[1] },
    { name: l.testimonial3Name, age: l.testimonial3Age, role: l.testimonial3Role, before: l.testimonial3Before, after: l.testimonial3After, quote: l.testimonial3Quote, rating: 5, color: AVATAR_COLORS[2] },
  ];

  const faq2Answer = (
    <>
      {l.faq2A_p1}{format(9.99)}{l.faq2A_p2}{format(29.99)}{l.faq2A_p3}{format(39.99)}{l.faq2A_p4}
    </>
  );

  const FAQ = [
    { question: l.faq1Q, answer: l.faq1A },
    { question: l.faq2Q, answer: faq2Answer },
    { question: l.faq3Q, answer: l.faq3A },
    { question: l.faq4Q, answer: l.faq4A },
    { question: l.faq5Q, answer: l.faq5A },
    { question: l.faq6Q, answer: l.faq6A },
    { question: l.faq7Q, answer: l.faq7A },
    { question: l.faq8Q, answer: l.faq8A },
  ];

  return (
    <div className="min-h-screen bg-[#111827] text-white overflow-x-hidden">

      {/* STICKY MOBILE BAR */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-[#111827]/98 border-t border-orange-500/30 px-4 py-3 flex items-center justify-between gap-3" data-testid="sticky-mobile-bar">
        <div className="text-xs text-gray-400 leading-tight">
          <span>{l.stickySecure}</span>
        </div>
        <button onClick={() => handleGetStarted('decouverte')} className="flex-shrink-0 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 rounded-xl font-bold text-sm text-white transition-all whitespace-nowrap" data-testid="sticky-cta">
          {l.stickyPriceFrom} {format(9.99)}
        </button>
      </div>

      {/* HEADER STICKY */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#111827]/95 backdrop-blur-md border-b border-gray-800">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <span className="text-2xl font-bold text-orange-500">BPM</span>
            <span className="hidden sm:inline text-sm text-gray-500">Beyond Personal Matrix</span>
          </div>
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#tests" className="text-gray-400 hover:text-white text-sm transition-colors">{t.header.tests}</a>
            <a href="#pricing" className="text-gray-400 hover:text-white text-sm transition-colors">{t.header.pricing}</a>
            <a href="#testimonials" className="text-gray-400 hover:text-white text-sm transition-colors">{t.header.testimonials}</a>
            <a href="#faq" className="text-gray-400 hover:text-white text-sm transition-colors">{t.header.faq}</a>
          </nav>
          <div className="flex items-center space-x-3">
            <CurrencySelector className="hidden sm:flex" />
            <LanguageSelector />
            <a href="/login" className="hidden sm:block text-gray-400 hover:text-white text-sm transition-colors">{t.header.login}</a>
            <button onClick={() => handleGetStarted('complete')} className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 rounded-xl transition-all text-sm font-semibold" data-testid="header-cta">
              {t.header.start}
            </button>
          </div>
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-24">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-3 mb-8">
              <div
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 rounded-full text-orange-400 text-sm cursor-help"
                title={l.badgeMethodology}
                data-testid="live-counter-badge"
              >
                <Star className="w-4 h-4 fill-orange-400" />
                <span className="font-medium">
                  +{liveCount.toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US')} {language === 'fr' ? 'profils analysés' : 'profiles analyzed'}
                </span>
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500/15 rounded-full text-green-400 text-xs border border-green-500/20">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
                <span>{l.discoveryBadgeFrom} {format(9.99)}, {l.discoveryBadge}</span>
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">
              {l.heroTitle1}<br />
              <span className="text-orange-500">{l.heroTitle2}</span>
            </h1>

            <p className="text-lg sm:text-xl text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">{l.heroSubtitle}</p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <button
                onClick={() => { trackCta('discovery'); handleGetStarted('decouverte'); }}
                className="px-8 py-4 bg-orange-500 hover:bg-orange-600 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 shadow-lg shadow-orange-500/30"
                data-testid="hero-cta-primary"
              >
                {l.heroCtaPrimary}, {format(9.99)}
                <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href="#pricing"
                onClick={() => trackCta('see_pricing')}
                className="px-8 py-4 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2"
                data-testid="hero-cta-secondary"
              >
                {l.heroCtaSecondary}
              </a>
            </div>

            <p className="text-sm text-gray-400 mb-8">{l.heroDeliverable}</p>

            <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400">
              <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-green-500" /><span>{l.trustSecure}</span></div>
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-blue-500" /><span>{l.trustImmediate}</span></div>
              <div className="flex items-center gap-2"><Award className="w-4 h-4 text-orange-500" /><span>{l.trustGuarantee}</span></div>
              <div className="flex items-center gap-2" data-testid="trust-gdpr"><Shield className="w-4 h-4 text-purple-400" /><span>{l.trustGdpr}</span></div>
              <div className="flex items-center gap-2" data-testid="trust-ai"><Zap className="w-4 h-4 text-yellow-400" /><span>{l.trustAi}</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* 3 PILLARS */}
      <section className="relative py-16 bg-[#1F2937]/30">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {PILLARS.map((pillar, i) => (
              <div key={i} className="p-8 rounded-2xl bg-[#1F2937] border border-gray-700 hover:border-orange-500/50 transition-all text-center">
                <div className="text-4xl mb-4">{i === 0 ? '\uD83C\uDFAF' : i === 1 ? '\uD83D\uDCAA' : '\uD83E\uDDED'}</div>
                <h3 className="text-xl font-bold mb-3 text-white">{pillar.title}</h3>
                <p className="text-gray-400">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative py-20 border-y border-gray-800">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">
              {l.howTitle} <span className="text-orange-500">{l.howTitleHighlight}</span>
            </h2>
            <p className="text-gray-400">{l.howSubtitle}</p>
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="hidden md:grid grid-cols-4 gap-6 relative">
              <div className="absolute top-10 left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-orange-500/20 via-orange-500/60 to-orange-500/20 z-0"></div>
              {HOW_STEPS.map((s, i) => (
                <div key={i} className="relative z-10 flex flex-col items-center text-center">
                  <div className="w-20 h-20 rounded-2xl bg-[#1F2937] border-2 border-orange-500/50 flex items-center justify-center text-3xl mb-4 shadow-lg shadow-orange-500/10">
                    {s.emoji}
                  </div>
                  <div className="text-xs text-orange-500 font-bold mb-1 tracking-widest">{l.step} {s.step}</div>
                  <h4 className="font-bold text-white text-sm mb-2 leading-snug">{s.title}</h4>
                  <p className="text-gray-500 text-xs leading-snug">{s.sub}</p>
                </div>
              ))}
            </div>
            <div className="md:hidden space-y-4">
              {HOW_STEPS.map((s, i) => (
                <div key={i} className="flex items-start gap-4 p-5 rounded-xl bg-[#1F2937] border border-gray-700">
                  <div className="text-2xl flex-shrink-0">{s.emoji}</div>
                  <div>
                    <div className="text-xs text-orange-500 font-bold mb-0.5 tracking-wider">{l.step} {s.step}</div>
                    <h4 className="font-bold text-white text-sm mb-1">{s.title}</h4>
                    <p className="text-gray-500 text-xs">{s.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="relative py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                {l.painTitle} <span className="text-orange-500">{l.painTitleHighlight}</span>
              </h2>
            </div>
            <div className="space-y-4">
              {PAIN_POINTS.map((point, i) => (
                <div key={i} className="flex items-center gap-4 p-5 rounded-xl bg-[#1F2937] border border-gray-700">
                  <X className="w-6 h-6 text-red-500 flex-shrink-0" />
                  <p className="text-gray-300">{point}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-xl text-gray-400 mt-10">
              <span className="text-white font-semibold">{l.painConclusion}</span>
            </p>
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section className="relative py-20 bg-[#1F2937]/30">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                {l.solutionTitle} <span className="text-orange-500">{l.solutionTitleHighlight}</span>
              </h2>
            </div>
            <div className="space-y-4">
              {SOLUTIONS.map((point, i) => (
                <div key={i} className="flex items-center gap-4 p-5 rounded-xl bg-[#111827] border border-gray-700">
                  <Check className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <p className="text-gray-300">{point}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <p className="text-xl">
                <span className="text-white">{l.solutionConclusion1}</span>
                <span className="text-orange-500 font-bold">{l.solutionConclusion2}</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6 TESTS SECTION */}
      <section id="tests" className="relative py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {l.testsTitle} <span className="text-orange-500">{l.testsTitleHighlight}</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">{l.testsSubtitle}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {TESTS.map((test) => (
              <div key={test.id} className={`p-6 rounded-xl bg-[#1F2937] border transition-all ${test.id === 'mbti' ? 'border-orange-500/50 ring-1 ring-orange-500/20' : 'border-gray-700 hover:border-orange-500/50'}`}>
                <div className="flex items-center gap-3 mb-4">
                  {testIcons[test.id] ? (
                    <picture>
                      <source srcSet={testIcons[test.id].replace('.png', '.webp')} type="image/webp" />
                      <img src={testIcons[test.id]} alt={test.name} className="w-12 h-12 object-contain" loading="lazy" />
                    </picture>
                  ) : (
                    <SchwartzIcon />
                  )}
                  <div>
                    <h4 className="text-xl font-bold">{test.name}</h4>
                    {test.id === 'mbti' && (
                      <span className="text-xs text-orange-400">{l.testsMbtiTag}</span>
                    )}
                  </div>
                </div>
                <p className="text-gray-400 text-sm">{test.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI DISCLAIMER */}
      <div className="container mx-auto px-6 pt-8">
        <div
          className="max-w-4xl mx-auto p-4 rounded-lg bg-blue-500/5 border border-blue-500/20 text-sm text-gray-400 flex items-start gap-3"
          data-testid="ai-disclaimer"
        >
          <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <p>{l.aiDisclaimer}</p>
        </div>
      </div>

      {/* AI ANALYSIS SECTION */}
      <section className="relative py-20 bg-[#1F2937]/30">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 rounded-full text-blue-400 text-sm mb-6">
                <Zap className="w-4 h-4" />
                <span>{l.aiTag}</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                {l.aiTitle} <span className="text-orange-500">{l.aiTitleHighlight}</span>
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto">{l.aiSubtitle}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-10 items-start">
              <div className="space-y-4">
                {AI_FEATURES.map((feature, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-[#111827] border border-gray-700">
                    <div className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0"></div>
                    <p className="text-gray-300">{feature}</p>
                  </div>
                ))}
                <div className="mt-6 p-4 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center gap-3">
                  <Zap className="w-5 h-5 text-orange-400 flex-shrink-0" />
                  <p className="text-orange-300 text-sm font-medium">{l.aiDashboard}</p>
                </div>
              </div>
              <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-900/30 to-orange-900/30 border border-purple-500/30 mt-6">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-10 h-10 text-purple-400" />
                  <div>
                    <h3 className="text-xl font-bold text-white">{l.aiDuoTitle}</h3>
                    <p className="text-purple-300 text-sm">{l.aiDuoSubtitle}</p>
                  </div>
                </div>
                <p className="text-gray-300 mb-4">{l.aiDuoDesc}</p>
                <ul className="space-y-2">
                  {DUO_FEATURES.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                      <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FREE PREVIEW (LEAD MAGNET) */}
      <FreePreviewCTA />

      {/* PRICING SECTION */}
      <section id="pricing" className="relative py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {l.pricingTitle} <span className="text-orange-500">{l.pricingTitleHighlight}</span>
            </h2>
            <p className="text-gray-400">{l.pricingSubtitle}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto items-start">
            {PRODUCTS.map((product) => (
              <div key={product.id} data-testid={`product-${product.id}`} className={`relative p-8 rounded-2xl transition-all ${product.highlighted ? 'bg-[#1F2937] border-2 border-orange-500 scale-105 shadow-xl shadow-orange-500/10' : 'bg-[#1F2937] border border-gray-700 hover:border-gray-600'}`}>
                {product.badge && (
                  <div className={`absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 text-sm font-bold rounded-full text-white ${product.badgeColor === 'orange' ? 'bg-orange-500' : 'bg-green-500'}`}>
                    {product.badge}
                  </div>
                )}
                <div className="text-center mb-6 pt-2">
                  <p className="text-gray-500 text-sm mb-1">{product.name}</p>
                  <h3 className="text-2xl font-bold text-white">{product.subtitle}</h3>
                </div>
                <div className="text-center mb-2">
                  <div className="flex items-baseline justify-center">
                    <span className="text-5xl font-bold text-orange-500" data-testid={`product-${product.id}-price`}>{format(product.price)}</span>
                  </div>
                  {product.originalPrice && <p className="text-gray-600 line-through text-lg mt-1">{format(product.originalPrice)}</p>}
                  {product.pricePerPersonAmount && <p className="text-gray-500 text-sm mt-2">{format(product.pricePerPersonAmount)} {product.pricePerPersonSuffix}</p>}
                </div>
                <p className="text-center text-xs text-gray-500 mb-4">{l.pricingReassurance}</p>
                <div className="h-px bg-gray-700 mb-5"></div>
                <ul className="space-y-3 mb-8">
                  {product.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className={feature.startsWith('\u2B50') ? 'text-purple-300 font-medium' : 'text-gray-300'}>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => handleCheckout(product.id)} disabled={checkoutLoading === product.id} className={`w-full py-4 rounded-xl font-semibold text-lg transition-all ${product.buttonStyle === 'primary' || product.buttonStyle === 'primary-orange' ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/20' : 'bg-gray-700 hover:bg-gray-600 text-white'} ${checkoutLoading === product.id ? 'opacity-50 cursor-not-allowed' : ''}`} data-testid={`product-${product.id}-cta`}>
                  {checkoutLoading === product.id ? l.pricingLoading : `${product.buttonText}, ${format(product.price)}`}
                </button>
              </div>
            ))}
          </div>
          <div className="text-center mt-10 space-y-3">
            <div className="inline-flex items-center gap-2 text-green-400">
              <Shield className="w-5 h-5" />
              <span className="font-medium">{l.pricingGuarantee}</span>
            </div>
            <p className="text-gray-500 text-sm">{l.pricingQuestion}</p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="relative py-20 bg-[#1F2937]/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {l.testimonialsTitle} <span className="text-orange-500">{l.testimonialsTitleHighlight}</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {TESTIMONIALS.map((tm, i) => (
              <div key={i} className="p-6 rounded-xl bg-[#111827] border border-gray-700 flex flex-col">                <div className="flex gap-1 mb-4">
                  {[...Array(tm.rating)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 text-orange-500 fill-orange-500" />
                  ))}
                </div>
                <div className="mb-4 space-y-2 flex-1">
                  <div className="flex items-start gap-2">
                    <span className="text-red-400 text-xs font-bold uppercase tracking-wider flex-shrink-0 mt-0.5">{l.before}</span>
                    <p className="text-gray-400 text-sm">{tm.before}</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-green-400 text-xs font-bold uppercase tracking-wider flex-shrink-0 mt-0.5">{l.after}</span>
                    <p className="text-gray-300 text-sm">{tm.after}</p>
                  </div>
                  <p className="text-white text-sm italic mt-3 border-l-2 border-orange-500/50 pl-3">"{tm.quote}"</p>
                </div>
                <div className="flex items-center gap-4 border-t border-gray-800 pt-4">
                  <InitialsAvatar name={tm.name} color={tm.color} />
                  <div className="text-sm">
                    <p className="font-semibold text-white">{tm.name}, {tm.age}</p>
                    <p className="text-gray-400">{tm.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-gray-500 mt-8 max-w-2xl mx-auto" data-testid="testimonials-note">
            {l.testimonialsNote}
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                {l.faqTitle} <span className="text-orange-500">{l.faqTitleHighlight}</span>
              </h2>
            </div>
            <div className="space-y-4">
              {FAQ.map((item, i) => (
                <div key={i} className="rounded-xl border border-gray-700 overflow-hidden bg-[#1F2937]">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full p-6 flex justify-between items-center text-left hover:bg-[#1F2937]/80 transition-colors" data-testid={`faq-item-${i}`}>
                    <span className="font-semibold text-white pr-4">{item.question}</span>
                    <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === i && <div className="px-6 pb-6 text-gray-400">{item.answer}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-20 bg-[#1F2937]/30">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {l.finalTitle} <span className="text-orange-500">{l.finalTitleHighlight}</span>
            </h2>
            <p className="text-gray-400 mb-8 text-lg">{l.finalSubtitle}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => handleGetStarted('decouverte')} className="px-8 py-4 bg-gray-700 hover:bg-gray-600 rounded-xl font-semibold text-lg transition-all" data-testid="final-cta-discovery">
                {l.finalDiscoveryBtn}, {format(9.99)}
              </button>
              <button onClick={() => handleGetStarted('complete')} data-testid="final-cta" className="inline-flex items-center gap-2 px-10 py-4 bg-orange-500 hover:bg-orange-600 rounded-xl font-bold text-lg transition-all shadow-xl shadow-orange-500/30">
                {l.finalCompleteBtn}, {format(29.99)}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-500 mt-6 text-sm">{l.finalReassurance}</p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative py-8 border-t border-gray-800 mb-16 md:mb-0">
        <div className="container mx-auto px-6">
          <p
            className="text-xs text-gray-600 max-w-3xl mx-auto text-center mb-4"
            data-testid="trademark-note"
          >
            {l.trademarkNote}
          </p>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center space-x-2">
              <span className="text-xl font-bold text-orange-500">BPM</span>
              <span className="text-sm text-gray-500">Beyond Personal Matrix</span>
            </div>
            <div className="flex gap-6 text-sm text-gray-500">
              <Link to="/mentions-legales" className="hover:text-white transition-colors" data-testid="footer-legal-notices">{l.legalNotices}</Link>
              <Link to="/cgv" className="hover:text-white transition-colors" data-testid="footer-terms">{l.terms}</Link>
              <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">{l.privacy}</Link>
            </div>
            <p className="text-sm text-gray-600">{l.copyright}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default SalesLanding;
